import { Check, Minus } from "lucide-react";

import { Card, Section, SectionHeader, StatusBadge } from "@/components/ds";
import { Reveal, RevealGroup } from "@/components/motion/Reveal";
import { cn } from "@/lib/utils";
import {
  CATEGORY_COMPARISON_NOTE,
  CATEGORY_LEAD,
  CATEGORY_LINES,
  CATEGORY_NAME,
  CATEGORY_SCOPE_NOTE,
  CATEGORY_STATEMENT,
  categoryCapabilities,
  categoryEras,
  categoryTiers,
} from "@/content/category";

/**
 * Category-leadership narrative for Intelligent Resuscitation Infrastructure.
 *
 * `headingLevel` keeps the section's headings correct for whichever page hosts
 * it (the page's own H1 always stays outside this component).
 */
type Props = {
  /** Renders without the outer <Section> padding, for pages with their own container. */
  bare?: boolean;
  className?: string;
};

const Statement = () => (
  <Reveal>
    <figure className="mt-10 rounded-2xl border border-primary/25 bg-primary/5 p-6 sm:p-8">
      <blockquote className="ds-h4 max-w-4xl text-balance font-semibold text-foreground">
        “{CATEGORY_STATEMENT}”
      </blockquote>
      <figcaption className="ds-caption mt-4 text-muted-foreground">
        The category statement for {CATEGORY_NAME}, as defined by PathFree Technologies.
      </figcaption>
    </figure>
  </Reveal>
);

const Evolution = () => (
  <div className="mt-12">
    <h3 className="ds-h3">How resuscitation equipment has evolved</h3>
    <RevealGroup as="ol" className="mt-6 grid gap-4 lg:grid-cols-4">
      {categoryEras.map((era, i) => (
        <Reveal as="li" inGroup key={era.id} className="h-full">
          <Card
            className={cn(
              "flex h-full flex-col gap-3",
              era.id === "infrastructure" && "border-primary/40 bg-primary/5",
            )}
          >
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className="inline-flex size-8 items-center justify-center rounded-full border border-primary/30 bg-primary/10 font-mono text-xs font-semibold text-primary"
              >
                {i + 1}
              </span>
              <span className="ds-label text-muted-foreground">{era.era}</span>
            </div>
            <h4 className="ds-h5 text-balance">{era.title}</h4>
            <p className="ds-body text-muted-foreground">{era.body}</p>
            <div className="mt-auto pt-2">
              <StatusBadge tone={era.id === "infrastructure" ? "info" : "neutral"}>{era.scope}</StatusBadge>
            </div>
          </Card>
        </Reveal>
      ))}
    </RevealGroup>
  </div>
);

const Comparison = () => (
  <div className="mt-14">
    <h3 className="ds-h3">What each approach is designed to cover</h3>
    <p className="ds-body mt-3 max-w-3xl text-muted-foreground">
      A capability-scope comparison across four approaches to resuscitation support.
    </p>

    {/* Keyboard users must be able to scroll the wide table (WCAG 2.1.1). */}
    <div
      className="mt-6 overflow-x-auto rounded-2xl border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      tabIndex={0}
      role="group"
      aria-label="Capability scope comparison table, scrollable horizontally"
    >
      <table className="w-full min-w-[720px] border-collapse text-left">
        <caption className="sr-only">
          Capability scope by approach: traditional code cart, point solution, connected system, and the
          PathFree vision for {CATEGORY_NAME}.
        </caption>
        <thead>
          <tr className="bg-surface">
            <th scope="col" className="p-4 align-bottom text-sm font-semibold text-foreground">
              Capability
            </th>
            {categoryTiers.map((tier) => (
              <th
                key={tier.id}
                scope="col"
                className={cn(
                  "p-4 align-bottom text-sm font-semibold text-foreground",
                  tier.emphasis && "bg-primary/10",
                )}
              >
                <span className="block">{tier.label}</span>
                <span className="ds-caption mt-1 block font-normal text-muted-foreground">{tier.summary}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categoryCapabilities.map((capability) => (
            <tr key={capability.id} className="border-t border-border">
              <th scope="row" className="p-4 text-sm font-medium text-foreground">
                {capability.label}
              </th>
              {categoryTiers.map((tier) => {
                const inScope = tier.scope[capability.id];
                return (
                  <td key={tier.id} className={cn("p-4", tier.emphasis && "bg-primary/5")}>
                    {inScope ? (
                      <span className="inline-flex items-center gap-2 text-sm text-foreground">
                        <Check aria-hidden className="size-4 text-primary" />
                        In scope
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                        <Minus aria-hidden className="size-4" />
                        Not in scope
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <p className="ds-legal mt-4 max-w-3xl text-muted-foreground">{CATEGORY_COMPARISON_NOTE}</p>
  </div>
);

const Lines = () => (
  <RevealGroup as="ul" className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
    {CATEGORY_LINES.map((line) => (
      <Reveal as="li" inGroup key={line}>
        <Card tone="mist" className="h-full">
          <p className="ds-h5 text-balance text-foreground">{line}</p>
        </Card>
      </Reveal>
    ))}
  </RevealGroup>
);

const Body = () => (
  <>
    <Statement />
    <Evolution />
    <Comparison />
    <Lines />
    <p className="ds-legal mt-6 max-w-3xl text-muted-foreground">{CATEGORY_SCOPE_NOTE}</p>
  </>
);

export const CategoryLeadership = ({ bare, className }: Props) => {
  const header = (
    <SectionHeader
      eyebrow="Category leadership"
      title={<span id="category-heading">Defining Intelligent Resuscitation Infrastructure</span>}
      description={CATEGORY_LEAD}
    />
  );

  if (bare) {
    return (
      <section aria-labelledby="category-heading" className={cn("mt-16", className)}>
        <h2 className="ds-h2" id="category-heading">
          Defining Intelligent Resuscitation Infrastructure
        </h2>
        <p className="ds-body mt-3 max-w-3xl text-muted-foreground">{CATEGORY_LEAD}</p>
        <Body />
      </section>
    );
  }

  return (
    <Section tone="mist" aria-labelledby="category-heading" className={className}>
      {header}
      <Body />
    </Section>
  );
};

export default CategoryLeadership;
